<template>
	<div class="content-wrapper">
		<!-- Content -->

		<div class="container flex-grow-1 container-p-y">
			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('trainings.list.title') }}</h4>
					<p class="mb-0">{{ $t('trainings.list.description') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4" v-if="isManager">
					<router-link to="/trainings/create-individual">
						<button type="button" class="btn btn-outline-primary">
							<i class="ri-user-line me-1"></i> {{ $t('trainings.list.orderIndividual') }}
						</button>
					</router-link>
					<router-link to="/trainings/create">
						<button type="button" class="btn btn-primary">
							{{ $t('trainings.list.orderTrainings') }}
						</button>
					</router-link>
				</div>
			</div>

			<div class="col-lg-12">
				<div class="card h-100">
					<div class="card-body pb-0">
						<form class="dt_adv_search">
							<div class="row">
								<div class="col-12">
									<div class="row g-5">
										<!-- Recherche -->
										<div
											:class="displayAgencies ? 'col-6 col-sm-6 col-lg-6' : 'col-8 col-sm-8 col-lg-8'"
										>
											<div class="form-floating form-floating-outline">
												<input
													type="text"
													class="form-control dt-input dt-full-name"
													placeholder="11.2.6.2"
													v-model="search.content"
													@input="applyFilters"
													autocomplete="off"
												/>
												<label>{{ $t('trainings.list.searchPlaceholder') }}</label>
											</div>
										</div>

										<!-- Status -->
										<div
											:class="displayAgencies ? 'col-3 col-sm-3 col-lg-3' : 'col-4 col-sm-4 col-lg-4'"
										>
											<div class="form-floating form-floating-outline">
												<select
													id="statusSelect"
													class="form-select"
													v-model="search.status"
													@change="applyFilters"
												>
													<option value="">{{ $t('trainings.list.allTrainings') }}</option>
													<option value="in_progress">{{ $t('trainings.list.inProgress') }}</option>
													<option value="succeeded">{{ $t('trainings.list.completed') }}</option>
													<option value="failed">{{ $t('trainings.list.failed') }}</option>
													<option value="cancelled">{{ $t('trainings.list.cancelled') }}</option>
													<option value="expired">{{ $t('trainings.list.expired') }}</option>
												</select>
												<label>{{ $t('trainings.list.statusLabel') }}</label>
											</div>
										</div>

										<!-- Filiales -->
										<div
											v-if="displayAgencies"
											class="col-3 col-sm-3 col-lg-3"
										>
											<div class="form-floating form-floating-outline">
												<select
													id="agencySelect"
													class="form-select"
													v-model="search.agency"
													@change="applyFilters"
												>
													<option value="">{{ $t('trainings.list.allAgencies') }}</option>
													<option
														v-for="agency in agencies"
														:key="agency"
														:value="agency"
													>
														{{ agency }}
													</option>
												</select>
												<label>{{ $t('trainings.list.agencies') }}</label>
											</div>
										</div>
									</div>
								</div>
							</div>
						</form>
					</div>
					<div class="table-responsive">
						<table class="table table-borderless">
							<thead>
								<tr class="border-bottom">
									<th class="bg-transparent fs-xsmall">{{ $t('trainings.list.training') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('trainings.list.trainee') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('trainings.list.type') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('trainings.list.statusLabel') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('trainings.list.progress') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(trainee, index) in trainees" :key="index">
									<td>
										<div class="d-flex justify-content-start align-items-center">
											<div class="avatar avatar me-4">
												<span class="avatar-initial rounded-circle bg-label-primary">
													<i class="ri-flight-takeoff-line"></i>
												</span>
											</div>
											<div>
												<h6 class="mb-0 text-truncate" data-bs-toggle="tooltip" :data-bs-title="`Formation ${trainee.formationTitle}`">
													Formation {{ trainee.formationTitle }}
													<span v-if="trainee.language === 'en'">(🇺🇸)</span>
												</h6>

												<p class="text-truncate mb-0">{{ formatRelativeDate(trainee.createdAt) }}</p>
											</div>
										</div>
									</td>
									<td>
										{{ trainee.user.firstName }} {{ trainee.user.lastName }}
										<p class="text-truncate mb-0" style="font-size: 12px;" v-if="trainee.user.agency">
											({{ trainee.user.agency }})
										</p>
									</td>
									<td>
										<span 
											v-if="trainee.type === 'renewal'"
										>
											{{ $t('common.trainingTypes.renewal') }}
										</span>
										<span v-else>
											{{ $t('common.trainingTypes.initial') }}
										</span>
									</td>
									<td>
										<span :class="getStatusInfo(trainee.status).class">
											{{ getStatusInfo(trainee.status).text }}
										</span>
									</td>
									<td>
										<div class="progress" data-bs-toggle="tooltip" :data-bs-title="calculateProgress(trainee) + '%'">
											<div
												class="progress-bar"
												role="progressbar"
												:style="{ width: calculateProgress(trainee) + '%' }"
												:aria-valuenow="calculateProgress(trainee)"
												aria-valuemin="0"
												aria-valuemax="100">
											</div>
										</div>
									</td>
									<td>
										<a
											href="javascript:void(0);"
											class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light"
											@click="openRequestResetModal(trainee)"
											data-bs-toggle="tooltip"
											:data-bs-title="$t('trainings.list.exceptionalRequest')"
											v-if="canRequestReset(trainee)"
										>
											<i class="ri-error-warning-line ri-22px"></i>
										</a>

										<a
											href="javascript:void(0);"
											class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light"
											@click="loginModal(trainee)"
											data-bs-toggle="tooltip"
											:data-bs-title="$t('trainings.list.login')"
											v-if="canLogin(trainee)"
										>
											<i class="ri-login-circle-line"></i>
										</a>

										<a
											href="javascript:void(0);"
											class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light"
											@click="evaluationCodeModal(trainee)"
											data-bs-toggle="tooltip"
											:data-bs-title="$t('trainings.list.evaluationCode')"
											v-if="hasEvaluationCode(trainee)"
										>
											<i class="ri-rotate-lock-fill ri-22px"></i>
										</a>

										<a
											href="javascript:void(0);"
											class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light"
											@click="goToTraineeTimeline(trainee._id)"
											data-bs-toggle="tooltip"
											:data-bs-title="$t('trainings.list.viewTimeline')"
											v-if="canViewTimeline(trainee)"
										>
											<i class="ri-eye-line ri-22px"></i>
										</a>

										<a
											href="javascript:void(0);"
											class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light"
											@click="downloadCertificate(trainee._id)"
											v-if="canDownloadCertificate(trainee)"
											data-bs-toggle="tooltip"
											:data-bs-title="$t('trainings.list.downloadCertificate')"
										>
											<i class="ri-file-download-fill ri-22px"></i>
										</a>

										<a
											href="javascript:void(0);"
											class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light"
											v-if="canResendInvitation(trainee)"
											@click="resentTraineeInvitationEmail(trainee._id, trainee.user.email)"
											data-bs-toggle="tooltip"
											:data-bs-title="$t('trainings.list.resendInvitation')"
										>
											<i class="ri-loop-right-fill ri-22px"></i>
										</a>

										<a
											href="javascript:void(0);"
											class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light"
											v-if="canCancel(trainee)"
											@click="cancelModal(trainee)"
											data-bs-toggle="tooltip"
											:data-bs-title="$t('trainings.list.cancelTraining')"
										>
											<i class="ri-close-circle-line ri-22px"></i>
										</a>
									</td>
								</tr>
							</tbody>
						</table>
						<div class="row mt-5" v-if="pagination.totalEntries > 5">
							<div class="col-sm-12 col-md-6">
								<div class="dataTables_info" id="DataTables_Table_0_info" role="status" aria-live="polite" style="padding-left: 15px;margin: 10px;">
									{{ $t('trainings.list.totalTrainings', { count: pagination.totalEntries }) }}
								</div>
							</div>
							<div class="col-sm-12 col-md-6 d-flex justify-content-end" style="padding-right: 40px;">
								<div class="pagination-container">
									<ul class="pagination">
										<!-- Bouton Précédent -->
										<li :class="['page-item', { disabled: pagination.page === 1 }]" @click="changePage(pagination.page - 1)">
											<a class="page-link">
												<i class="ri-arrow-left-s-line"></i>
											</a>
										</li>

										<!-- Pages dynamiques (affichage par groupe de 3) -->
										<li v-for="page in visiblePages" :key="page" :class="['page-item', { active: page === pagination.page }]" @click="changePage(page)">
											<a class="page-link">{{ page }}</a>
										</li>

										<!-- Bouton Suivant -->
										<li :class="['page-item', { disabled: pagination.page + 3 > pagination.totalPages }]" @click="changePage(pagination.page + 1)">
											<a class="page-link">
												<i class="ri-arrow-right-s-line"></i>
											</a>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- SUCCESS MODAL -->
			<div class="modal fade" id="editUser" tabindex="-1" aria-hidden="true" v-show="modalData">
				<div class="modal-dialog modal-dialog-centered1 modal-simple modal-add-new-cc">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">

							<div class="text-center mb-6">
								<CheckIcon />
								<p class="mt-5">{{ $t('trainings.modals.invitationSent', { email: modalData }) }}</p>
							</div>							
						</div>
					</div>
				</div>
			</div>

			<!-- LOGIN MODAL -->
			<div class="modal fade" id="modalLogin" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered1 modal-simple modal-add-new-cc" style="">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">

							<div class="text-center" v-if="selectedTraining">
								<i class="ri-id-card-line" style="font-size: 45px;"></i>
								<p class="mt-5 mb-0">{{ $t('trainings.modals.loginCode', { firstName: selectedTraining.user.firstName, lastName: selectedTraining.user.lastName }) }}</p>

								<h1 @click="copyCode(selectedTraining.token)" class="mb-0">
									{{ selectedTraining.token }}
								</h1>

								<transition name="slide-down">
									<span v-if="copied" class="copied-message mt-0 text-success">
										{{ $t('trainings.modals.loginCodeCopied') }}
									</span>
								</transition>

								<p>
									{{ $t('trainings.modals.loginCodeUsage') }}<br/>
									<a href="https://learn.pika-aero.com/login" target="_blank">learn.pika-aero.com/login</a>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- EVALUATION CODE MODAL -->
			<div class="modal fade" id="modalEvaluationCode" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered1 modal-simple modal-add-new-cc" style="">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">

							<div class="text-center" v-if="selectedTraining">
								<i class="ri-rotate-lock-fill" style="font-size: 50px;"></i>
								<p class="mt-5 mb-0">{{ $t('trainings.modals.evaluationCodeTitle', { firstName: selectedTraining.user.firstName, lastName: selectedTraining.user.lastName }) }}</p>

								<h1 @click="copyCode(selectedTraining.evaluationCode.code)" class="mb-0">
									{{ selectedTraining.evaluationCode?.code }}
								</h1>

								<transition name="slide-down">
									<span v-if="copied" class="copied-message mt-0 text-success">
										{{ $t('trainings.modals.loginCodeCopied') }}
									</span>
								</transition>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- CANCEL MODAL -->
			<div class="modal fade" id="modalCancel" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered1 modal-simple modal-add-new-cc" style="">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">

							<div class="text-center" v-if="requestSubmited">
								<CheckIcon class="mb-10" />
								<p class="mt-5 mb-0">
									{{ $t('trainings.modals.cancelSuccess') }}
								</p>
							</div>

							<div class="text-center" v-else>
								<i class="ri-close-circle-line" style="font-size: 60px;"></i>
								<p class="mt-5 mb-0" v-if="selectedTraining">
									{{ $t('trainings.modals.cancelConfirm', { formationName: selectedTraining.formationName, firstName: selectedTraining.user.firstName, lastName: selectedTraining.user.lastName }) }}
								</p>
								<br />
								<p>
									{{ $t('trainings.modals.cancelInfo') }}
								</p>
								<br />
							</div>
						</div>

						<div class="col-12 d-flex flex-wrap justify-content-center gap-4 row-gap-4" v-if="!requestSubmited">
							<button type="submit" class="btn btn-primary" @click="confirmCancel()">
								{{ $t('common.actions.confirm') }}
							</button>
							<button
								type="reset"
								class="btn btn-outline-secondary"
								data-bs-dismiss="modal"
								aria-label="Close">
								{{ $t('common.actions.cancel') }}
							</button>
						</div>

					</div>
				</div>
			</div>

			<!-- RESET MODAL -->
			<div class="modal fade" id="modalReset" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered1 modal-simple modal-add-new-cc" style="">
					<div class="modal-content">
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						<div class="modal-body p-0">

							<div class="text-center" v-if="requestSubmited">
								<CheckIcon class="mb-10" />
								<p class="mt-5 mb-0" v-if="resetData">
									{{ $t('trainings.modals.resetSuccess') }}
								</p>
							</div>

							<div class="text-center" v-else>
								<i class="ri-questionnaire-line" style="font-size: 45px;"></i>
								<p class="mt-5 mb-0" v-if="resetData">
									{{ $t('trainings.modals.resetTitle', { firstName: resetData.user.firstName, lastName: resetData.user.lastName }) }}
								</p>
								<p class="mt-5 mb-0">
									{{ $t('trainings.modals.resetInstruction') }}
								</p>

								<div class="form-floating form-floating-outline mt-15">
									<textarea
										class="form-control h-px-100 mt-10"
										id="reasonForReset"
										v-model="reasonForReset"
										:placeholder="$t('trainings.modals.resetReasonPlaceholder')"></textarea>
									<label for="exampleFormControlTextarea1">{{ $t('trainings.modals.resetReasonLabel') }}</label>
								</div>

								<button type="submit" class="btn btn-primary mt-10" @click="requestReset()">{{ $t('trainings.modals.resetSubmit') }}</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- / Content -->
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";
import CheckIcon from '@/components/CheckIcon.vue';

import { Tooltip } from 'bootstrap'

moment.locale(localStorage.getItem('locale') || 'fr');

export default {
	name: "TrainingList",

	components: { CheckIcon },

	data() {
		return {
			stats: null,
			trainees: [],
			modalData: null,
			selectedTraining: null,
			resetData: null,
			reasonForReset: null,
			requestSubmited: false,

			displayCancel: false,
			displayReset: false,
			displayAgencies: false,
			agencies: [],

			copied: false,
			search: {
				content: "",
				status: "in_progress",
				agency: "",
			},

			profile: null,

			pagination: {
				page: 1,
				limit: 10,
				totalEntries: 0,
				totalPages: Math.ceil(99 / 10)
			},

			isInitialized: false, // Pour éviter les appels multiples au montage
		};
	},

	computed: {
		visiblePages() {
			const start = Math.floor((this.pagination.page - 1) / 3) * 3 + 1;
			const end = Math.min(start + 2, this.pagination.totalPages);
			return Array.from({ length: end - start + 1 }, (_, i) => start + i);
		},
		startEntry() {
			return (this.pagination.page - 1) * this.pagination.limit + 1;
		},
		endEntry() {
			return Math.min(this.pagination.page * this.pagination.limit, this.pagination.totalEntries);
		},
		isManager() {
			return this.profile?.role === "manager";
		},
	},

	watch: {
		// Surveiller les changements de route pour mettre à jour les filtres
		'$route.query': {
			handler() {
				if (this.isInitialized) {
					this.syncFromUrl();
				}
			},
			deep: true
		}
	},

	methods: {
		// Synchroniser les paramètres depuis l'URL
		syncFromUrl() {
			const query = this.$route.query;

			this.pagination.page = parseInt(query.page) || 1;
			this.search.content = query.search || "";
			this.search.status = query.status || "in_progress";
			this.search.agency = query.agency || "";

			this.fetchTrainees();
		},

		// Mettre à jour l'URL avec les paramètres actuels
		updateUrl() {
			const query = {
				page: this.pagination.page,
			};

			// Ajouter les filtres seulement s'ils ont une valeur
			if (this.search.content) {
				query.search = this.search.content;
			}
			if (this.search.status) {
				query.status = this.search.status;
			}
			if (this.search.agency) {
				query.agency = this.search.agency;
			}

			// Éviter de pousser la même route
			if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
				this.$router.push({
					query,
					// Utiliser replace pour ne pas créer d'entrée dans l'historique à chaque changement
				}).catch(() => {});
			}
		},

		canLogin(trainee) {
			return ['in-progress', 'started', 'chapter-quiz', 'registered'].includes(trainee.status);
		},

		hasEvaluationCode(trainee) {
			if (trainee.status === 'cancelled')
				return false;

			return !!trainee.evaluationCode?.code;
		},

		canViewTimeline(trainee) {
			return trainee.status !== 'pending';
		},

		canDownloadCertificate(trainee) {
			if (trainee.status === 'succeeded' && trainee.certificateType !== 'dgr')
				return true;

			if (trainee.status === 'succeeded' && trainee.certificateType === 'dgr' && trainee.certificateReady)
				return true;

			return false;
		},

		canRequestReset(trainee) {
			if (trainee.status === 'failed' && this.displayReset && trainee.retries === 1)
				return true;

			return false;
		},

		canResendInvitation(trainee) {
			const excluded = ['succeeded', 'failed', 'pending', 'expired', 'cancelled'];
			return !excluded.includes(trainee.status);
		},

		canCancel(trainee) {
			const excluded = ['succeeded', 'failed', 'expired', 'cancelled'];
			return !excluded.includes(trainee.status) && this.displayCancel;
		},

		getStatusInfo(status) {
			if (status === "succeeded") {
				return {
					text: this.$t('trainings.status.succeeded'),
					class: 'badge bg-label-success rounded-pill'
				};
			}
			if (status === "registered") {
				return {
					text: this.$t('trainings.status.registered'),
					class: 'badge bg-label-primary rounded-pill'
				};
			}
			if (status === "started" || status === "in-progress" || status === "chapter-quiz" || status === "final-quiz") {
				return {
					text: this.$t('trainings.status.inProgress'),
					class: 'badge alert-warning rounded-pill'
				};
			}
			if (status === "pending") {
				return {
					text: this.$t('trainings.status.pending'),
					class: 'badge alert-warning rounded-pill'
				};
			}
			if (status === "failed") {
				return {
					text: this.$t('trainings.status.failed'),
					class: 'badge bg-label-danger rounded-pill'
				};
			}
			if (status === "expired") {
				return {
					text: this.$t('trainings.status.expired'),
					class: 'badge bg-label-danger rounded-pill'
				};
			}
			if (status === "cancelled") {
				return {
					text: this.$t('trainings.status.cancelled'),
					class: 'badge bg-label-danger rounded-pill'
				};
			}
			return {
				text: status,
				class: 'badge bg-label-primary rounded-pill'
			};
		},

		async copyCode(code) {
			try {
				await navigator.clipboard.writeText(code);
				this.copied = true;

				setTimeout(() => {
					this.copied = false;
				}, 1500);

			} catch (err) {
				console.error('Erreur lors de la copie :', err);
			}
		},

		calculateProgress(trainee) {
			if (!trainee.totalChapters || trainee.totalChapters === 0) return 0;
				return Math.min(100, Math.round((trainee.currentChapter / trainee.totalChapters) * 100));
		},
	
		formatRelativeDate(date) {
			return moment(date).fromNow();
		},

		goToTraineeTimeline(traineeId) {
			this.$router.push(`/trainings/timeline-trainee/${traineeId}`);
		},

		initTooltips() {
			const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
			tooltipTriggerList.forEach((el) => {
				const existingTooltip = Tooltip.getInstance(el);
				if (existingTooltip) {
					existingTooltip.dispose();
				}
			});

			this.$nextTick(() => {
				const newTooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
				newTooltipTriggerList.forEach((el) => {
					new Tooltip(el);
				});
			});
		},

		async fetchTrainees() {
			try {
				const params = {
					page: this.pagination.page,
					limit: this.pagination.limit,
					content: this.search.content || undefined,
					status: this.search.status || undefined,
					agency: this.search.agency || undefined
				};

				const response = await HTTP.get("/trainings/trainees", { params });

				this.trainees = response.data.trainees;
				this.stats = response.data.stats;

				// Display all if no trainees in progress
				if (this.trainees.length === 0 && this.search.status === "in_progress") {
					this.search.status = "";
					this.updateUrl(); // Mettre à jour l'URL
					this.fetchTrainees();
					return;
				}

				this.pagination.totalEntries = response.data.pagination.totalEntries;
				this.pagination.totalPages = response.data.pagination.totalPages;

				this.initTooltips();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async fetchFeaturesFlags() {
			try {
				const response = await HTTP.get('/company/flags');
				this.displayCancel = response.data.displayCancel;
				this.displayReset = response.data.displayReset;
				this.agencies = response.data.agencies;
				this.displayAgencies = response.data.displayAgencies;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async downloadCertificate(trainingId) {
			try {
				const response = await HTTP.post(`/certificates/download`, { trainingId });

				if (response.data?.base64) {
					const byteCharacters = atob(response.data.base64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: 'application/pdf' });

					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `attestation_${trainingId}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.message || 
					this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async resentTraineeInvitationEmail(trainingId, email) {
			try {
				await HTTP.post("/trainings/resent-invitation-email", { trainingId });

				this.modalData = email;

				const modalElement = document.getElementById("editUser");
				let modal = window.bootstrap.Modal.getInstance(modalElement);

				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}

				modal.show();

				setTimeout(() => {
					modal.hide();
				}, 2000)
			} catch (error) {
				const errorMessage = error?.response?.data?.message || 
					this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async requestReset() {
			try {
				if (!this.resetData)
					return;

				if (!this.reasonForReset)
					return alert(this.$t('trainings.modals.resetReasonRequired'));

				await HTTP.post("/trainings/request-test-reset", { trainingId: this.resetData._id, reason: this.reasonForReset });

				this.requestSubmited = true;

				const modalElement = document.getElementById("modalReset");
				let modal = window.bootstrap.Modal.getInstance(modalElement);

				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}

				modal.show();

				setTimeout(() => {
					modal.hide();
					this.requestSubmited = false;
				}, 3000)
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async confirmCancel() {
			try {
				if (!this.selectedTraining)
					return;

				await HTTP.delete(`/trainings/${this.selectedTraining._id}`);

				this.requestSubmited = true;

				const modalElement = document.getElementById("modalCancel");
				let modal = window.bootstrap.Modal.getInstance(modalElement);

				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}

				modal.show();
				this.fetchTrainees();

				setTimeout(() => {
					modal.hide();
					this.requestSubmited = false;
				}, 2000)
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async openRequestResetModal(training) {
			try {
				const modalElement = document.getElementById("modalReset");
				let modal = window.bootstrap.Modal.getInstance(modalElement);

				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}

				this.resetData = training;
				modal.show();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async loginModal(training) {
			try {
				this.selectedTraining = training;

				const modalElement = document.getElementById("modalLogin");
				let modal = window.bootstrap.Modal.getInstance(modalElement);

				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}

				modal.show();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async evaluationCodeModal(training) {
			try {
				this.selectedTraining = training;

				const modalElement = document.getElementById("modalEvaluationCode");
				let modal = window.bootstrap.Modal.getInstance(modalElement);

				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}

				modal.show();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async cancelModal(training) {
			try {
				this.selectedTraining = training;

				const modalElement = document.getElementById("modalCancel");
				let modal = window.bootstrap.Modal.getInstance(modalElement);

				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}

				modal.show();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async fetchProfile() {
			try {
				const response = await HTTP(`/user/profile`);
				this.profile = response.data.profile;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		// Méthode pour mettre à jour la page et relancer la recherche
		changePage(newPage) {
			if (newPage > 0 && newPage <= this.pagination.totalPages) {
				this.pagination.page = newPage;
				this.updateUrl(); // Mettre à jour l'URL
				this.fetchTrainees();
			}
		},

		// Méthode déclenchée par les inputs de recherche
		applyFilters() {
			this.pagination.page = 1; // Réinitialiser à la première page
			this.updateUrl(); // Mettre à jour l'URL
			this.fetchTrainees();
		}
	},

	async mounted() {
		// Initialiser depuis l'URL au montage
		this.syncFromUrl();
		await this.fetchFeaturesFlags();
		await this.fetchProfile();

		this.isInitialized = true;
	}
};
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
}

/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}

.copied-message {
	padding: 0.4rem 1rem;
	border-radius: 4px;
	display: inline-block;
	margin-top: 0.5rem;
	user-select: none;
}

/* Animation slideDown pour <transition> Vue */

.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.3s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
	max-height: 0;
	opacity: 0;
	padding-top: 0;
	padding-bottom: 0;
	margin-top: 0;
	overflow: hidden;
}
.slide-down-enter-to,
.slide-down-leave-from {
	max-height: 40px; /* Ajuste selon la hauteur de ton message */
	opacity: 1;
	padding-top: 0.4rem;
	padding-bottom: 0.4rem;
	margin-top: 0.5rem;
	overflow: hidden;
}

.modal-simple .modal-content {
		padding: 3rem;
}
</style>
